/**
 * Shopify Adapter
 * Hooks into Shopify events and extracts e-commerce data
 */

import { BaseAdapter } from './base-adapter';
import { ProductData, CartData, OrderData } from '../types';

declare const Shopify: any;
declare const ShopifyAnalytics: any;

export class ShopifyAdapter extends BaseAdapter {
  
  getName(): string {
    return 'Shopify';
  }

  detect(): boolean {
    return !!(
      typeof Shopify !== 'undefined' ||
      typeof ShopifyAnalytics !== 'undefined' ||
      document.querySelector('meta[content*="Shopify"]')
    );
  }

  init(): void {
    this.log('Initializing Shopify adapter');
  }

  hookAddToCart(callback: (product: ProductData) => void): void {
    // Intercept Shopify's cart add API calls
    const originalFetch = window.fetch;
    
    window.fetch = function(input: RequestInfo | URL, init?: RequestInit) {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;
      
      if (url && (url.includes('/cart/add.js') || url.includes('/cart/add'))) {
        return originalFetch.call(this, input, init).then((response: Response) => {
          // Clone response so we can read it
          response.clone().json().then(data => {
            const productData: ProductData = {
              productId: data.id?.toString() || data.product_id?.toString() || '',
              productSku: data.sku || '',
              productName: data.title || data.product_title || '',
              productPrice: data.price ? data.price / 100 : 0, // Shopify uses cents
              quantity: data.quantity || 1,
              variant: data.variant_title || ''
            };
            
            callback(productData);
          }).catch(() => {});
          
          return response;
        });
      }
      
      return originalFetch.call(this, input, init);
    };

    this.log('Add to cart hook installed');
  }

  hookProductView(callback: (product: ProductData) => void): void {
    // Check if on product page using Shopify Analytics
    if (
      typeof ShopifyAnalytics !== 'undefined' &&
      ShopifyAnalytics?.meta?.page?.pageType === 'product'
    ) {
      this.log('Product page detected via ShopifyAnalytics');

      const product = ShopifyAnalytics.meta.product;
      const productData: ProductData = {
        productId: product.id?.toString() || '',
        productSku: product.sku || '',
        productName: product.name || product.title || '',
        productPrice: product.price ? product.price / 100 : 0,
        productCategory: product.type || '',
        productUrl: window.location.href
      };

      callback(productData);
      return;
    }

    // Fallback: detect via URL pattern
    if (/\/products\//.test(window.location.pathname)) {
      this.log('Product page detected via URL pattern');
      
      const productData = this.extractProductFromPage();
      if (productData) {
        callback(productData);
      }
    }
  }

  hookViewCart(callback: (cart?: CartData) => void): void {
    // Detect Shopify cart page
    if (window.location.pathname.includes('/cart')) {
      this.log('Cart page detected');
      
      this.getCart().then(cart => {
        callback(cart || undefined);
      });
    }
  }

  hookCheckout(callback: (cart?: CartData) => void): void {
    // Shopify checkout is on separate domain (checkout.shopify.com)
    if (window.location.pathname.includes('/checkout')) {
      this.log('Checkout page detected');
      
      this.getCart().then(cart => {
        callback(cart || undefined);
      });
    }
  }

  hookPurchase(callback: (order: OrderData) => void): void {
    // Shopify checkout completion is on separate domain
    // Check if Shopify.checkout is available (thank you page)
    if (typeof Shopify !== 'undefined' && Shopify.checkout) {
      this.log('Order detected via Shopify.checkout');

      const checkout = Shopify.checkout;
      const orderData: OrderData = {
        orderId: checkout.order_id?.toString() || '',
        orderNumber: checkout.order_number?.toString() || '',
        orderValue: checkout.total_price ? checkout.total_price / 100 : 0,
        currency: checkout.currency || '',
        tax: checkout.total_tax ? checkout.total_tax / 100 : 0,
        shipping: checkout.shipping_rate?.price ? checkout.shipping_rate.price / 100 : 0,
        items: checkout.line_items?.map((item: any) => ({
          productId: item.product_id?.toString() || '',
          productSku: item.sku || '',
          productName: item.title || '',
          productPrice: item.price ? item.price / 100 : 0,
          quantity: item.quantity || 1
        })) || []
      };

      callback(orderData);
    }
  }

  async getCart(): Promise<CartData | null> {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      this.log('Fetched cart data', cart);

      return {
        cartId: cart.token,
        items: cart.items.map((item: any) => ({
          productId: item.product_id?.toString() || '',
          productSku: item.sku || '',
          productName: item.title || '',
          productPrice: item.price ? item.price / 100 : 0,
          quantity: item.quantity || 1,
          variant: item.variant_title || ''
        })),
        subtotal: cart.items_subtotal_price ? cart.items_subtotal_price / 100 : 0,
        total: cart.total_price ? cart.total_price / 100 : 0,
        currency: cart.currency || ''
      };
    } catch (error) {
      this.log('Failed to fetch cart', error);
      return null;
    }
  }

  /**
   * Extract product data from Shopify product page
   */
  private extractProductFromPage(): ProductData | null {
    try {
      // Try to find product JSON
      const productJsonElement = document.querySelector('script[type="application/ld+json"]');
      if (productJsonElement) {
        const productJson = JSON.parse(productJsonElement.textContent || '{}');
        if (productJson['@type'] === 'Product') {
          return {
            productId: productJson.sku || '',
            productName: productJson.name || '',
            productPrice: parseFloat(productJson.offers?.price || '0'),
            productUrl: productJson.url || window.location.href
          };
        }
      }

      // Fallback: parse from DOM
      const productName = document.querySelector('.product-title, .product__title, h1')?.textContent?.trim() || '';
      const priceElement = document.querySelector('.price, .product__price, [data-price]');
      const productPrice = priceElement ? 
        parseFloat(priceElement.textContent?.replace(/[^0-9.]/g, '') || '0') : 0;

      return {
        productId: '',
        productName,
        productPrice,
        productUrl: window.location.href
      };
    } catch (error) {
      this.log('Failed to extract product from page', error);
      return null;
    }
  }
}
